import { type BannerProps } from "@aurem/ui";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";

export type BannerItem = {
  id: string;
  props: BannerProps;
};

export type BannerContextType = {
  banners: BannerItem[];
  addBanner: (banner: BannerItem) => string;
  upsertBanner: (banner: BannerItem) => string;
  removeBanner: (id: string) => void;
};

export const BannerContext = createContext<BannerContextType | undefined>(
  undefined,
);

type Props = {
  children: ReactNode;
};

export const BannerProvider = ({ children }: Props) => {
  const [banners, setBanners] = useState<BannerItem[]>([]);

  const addBanner = useCallback((banner: BannerItem): string => {
    setBanners((prev) => {
      const index = prev.findIndex((b) => b.id === banner.id);
      if (index < 0) {
        return [...prev, banner];
      }
      return prev;
    });
    return banner.id;
  }, []);

  const upsertBanner = useCallback((banner: BannerItem): string => {
    setBanners((prev) => {
      const index = prev.findIndex((b) => b.id === banner.id);
      if (index < 0) {
        return [...prev, banner];
      }
      const updated = [...prev];
      updated[index] = banner;
      return updated;
    });
    return banner.id;
  }, []);

  const removeBanner = useCallback((id: string) => {
    setBanners((prev) => prev.filter((banner) => banner.id !== id));
  }, []);

  const value: BannerContextType = {
    banners,
    addBanner,
    upsertBanner,
    removeBanner,
  };

  return (
    <BannerContext.Provider value={value}>{children}</BannerContext.Provider>
  );
};

export const useBanner = (): BannerContextType => {
  const context = useContext(BannerContext);
  if (!context) {
    throw new Error("useBanner must be used within BannerProvider");
  }
  return context;
};
